import React, { useState, useEffect } from 'react';

const SlackChannelForm = ({ channel, orgId, teamId, onSave, onCancel, apiCall }) => {
  const isTeamMode = !!teamId && !orgId;
  const [formData, setFormData] = useState({
    channelName: '',
    webhookUrl: '',
    slackChannel: '',
    workspaceName: '',
    accessToken: '',
    isActive: true,
    isPrimary: false,
    includeActionButtons: true,
    mentionOnCritical: '',
    useThreadReplies: false,
    useOAuth: false,
    organizationId: orgId,
    teamId: teamId
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (channel) {
      loadChannelData();
    }
  }, [channel]);

  const loadChannelData = async () => {
    try {
      const endpoint = isTeamMode
        ? `/api/alertchannelsapi/team/${teamId}/${channel.id}`
        : `/api/alertchannelsapi/${channel.id}`;
      const data = await apiCall(endpoint);
      setFormData({
        channelName: data.channelName || '',
        webhookUrl: data.webhookUrl || '',
        slackChannel: data.slackChannel || '',
        workspaceName: data.workspaceName || '',
        accessToken: data.accessToken || '',
        isActive: data.isActive !== false,
        isPrimary: data.isPrimary === true,
        includeActionButtons: data.includeActionButtons !== false,
        mentionOnCritical: data.mentionOnCritical || '',
        useThreadReplies: data.useThreadReplies === true,
        useOAuth: !data.webhookUrl,
        organizationId: orgId,
        teamId: teamId
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.useOAuth && !formData.webhookUrl.trim()) {
      setError('Webhook URL is required when not using OAuth');
      return;
    }

    if (formData.webhookUrl) {
      try {
        new URL(formData.webhookUrl);
      } catch {
        setError('Invalid webhook URL format');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        channelId: channel?.id
      };

      if (channel) {
        // Update existing channel
        const updateEndpoint = isTeamMode
          ? `/api/alertchannelsapi/team/${teamId}/${channel.id}`
          : `/api/alertchannelsapi/${channel.id}`;
        await apiCall(updateEndpoint, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        // Create new channel
        const createEndpoint = isTeamMode
          ? '/api/alertchannelsapi/team/slack'
          : '/api/alertchannelsapi/slack';
        await apiCall(createEndpoint, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>{channel ? 'Edit' : 'Create'} Slack Channel</h3>
        <button
          type="button"
          onClick={onCancel}
          style={{
            border: 'none',
            background: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          ×
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '6px'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Channel Name *
          </label>
          <input
            type="text"
            value={formData.channelName}
            onChange={(e) => setFormData(prev => ({ ...prev, channelName: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.useOAuth}
              onChange={(e) => setFormData(prev => ({ ...prev, useOAuth: e.target.checked }))}
            />
            Use OAuth Integration (recommended)
          </label>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            OAuth provides better security and more features. If unchecked, you'll need to provide a webhook URL.
          </p>
        </div>

        {!formData.useOAuth && (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Webhook URL *
              </label>
              <input
                type="url"
                value={formData.webhookUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, webhookUrl: e.target.value }))}
                placeholder="https://hooks.slack.com/services/..."
                required={!formData.useOAuth}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Get your webhook URL from Slack App settings
              </p>
            </div>
          </>
        )}

        {formData.useOAuth && (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Access Token
              </label>
              <input
                type="password"
                value={formData.accessToken}
                onChange={(e) => setFormData(prev => ({ ...prev, accessToken: e.target.value }))}
                placeholder="xoxb-..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </>
        )}

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Slack Channel (optional)
          </label>
          <input
            type="text"
            value={formData.slackChannel}
            onChange={(e) => setFormData(prev => ({ ...prev, slackChannel: e.target.value }))}
            placeholder="#alerts or @username"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Workspace Name (optional)
          </label>
          <input
            type="text"
            value={formData.workspaceName}
            onChange={(e) => setFormData(prev => ({ ...prev, workspaceName: e.target.value }))}
            placeholder="My Workspace"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.includeActionButtons}
              onChange={(e) => setFormData(prev => ({ ...prev, includeActionButtons: e.target.checked }))}
            />
            Include Action Buttons (Acknowledge/Resolve)
          </label>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Mention on Critical Alerts (optional)
          </label>
          <input
            type="text"
            value={formData.mentionOnCritical}
            onChange={(e) => setFormData(prev => ({ ...prev, mentionOnCritical: e.target.value }))}
            placeholder="@channel, @here, or @username"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.useThreadReplies}
              onChange={(e) => setFormData(prev => ({ ...prev, useThreadReplies: e.target.checked }))}
            />
            Use Thread Replies for Updates
          </label>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.isPrimary}
              onChange={(e) => setFormData(prev => ({ ...prev, isPrimary: e.target.checked }))}
            />
            Set as Primary Channel
          </label>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Saving...' : (channel ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </form>
  );
};

export default SlackChannelForm;

